import type { FunctionComponent } from "react"

import dynamic from "next/dynamic"
import Image from "next/image"
import { useParams } from "next/navigation"
import { useState } from "react"

import { Heading } from "@jobteaser/spark/components/Heading"
import { Text } from "@jobteaser/spark/components/Text"
import { ElementGrid } from "@jobteaser/ui-kit/components/ElementGrid/ElementGrid"
import { Layout, LAYOUT_COLOR_BLOCK } from "@jobteaser/ui-kit/components/Layout/Layout"
import { BackLink } from "@jobteaser/ui-kit/components/Link/BackLink"
import { Link as LinkUiKit } from "@jobteaser/ui-kit/components/Link/Link"
import { Modal } from "@jobteaser/ui-kit/components/Modal/Modal"
import { RichText } from "@jobteaser/ui-kit/components/RichText/RichText"
import { Signature } from "@jobteaser/ui-kit/components/Signature/Signature"
import { Tag } from "@jobteaser/ui-kit/components/Tag/Tag"

import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { ARTICLE_TITLE_ID } from "@/modules/companies/client/components/ArticlesTitle/ArticlesTitle"
import { ARTICLE_SUBJECT_TYPES_I18N } from "@/modules/companies/client/constants/enums"
import { useCompanyArticleDetailsFetch } from "@/modules/companies/client/hooks/useCompanyArticleDetailsFetch"
import { useCompanyFetch } from "@/modules/companies/client/hooks/useCompanyFetch"
import { COMPANY_BASENAME, NEWSFEED_PAGE_ROUTE } from "@/modules/companies/client/utils/routing"
import { DATE_FORMATS, getDate } from "@/modules/dates/dates"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { Link } from "@/modules/routing/components/Link"

import styles from "./ArticleDetails.module.css"

const VideoDynamic = dynamic(() =>
  import("@/modules/companies/client/components/Media/Video").then(mod => mod.MediaVideo)
)

const ImageDynamic = dynamic(() =>
  import("@/modules/companies/client/components/Media/Image").then(mod => mod.MediaImage)
)

export const ArticleDetails: FunctionComponent = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalImageSrc, setModalImageSrc] = useState("")
  const { t } = useTranslation(["companies_client"])
  const { dataCompanyArticleDetails } = useCompanyArticleDetailsFetch()
  const { dataCareerCenter } = useCareerCenterFetch()
  const { dataCompany } = useCompanyFetch()
  const { companySlug } = useParams()
  const locale = useLocale()

  if (!dataCompanyArticleDetails) return null

  const openModal = (src: string): void => {
    setIsModalOpen(true)
    setModalImageSrc(src)
  }

  const closeModal = (): void => {
    setIsModalOpen(false)
    setModalImageSrc("")
  }

  const { title, content, createTime, images, video, externUrl, subject, targeted } = dataCompanyArticleDetails.article

  const hasVideo = !!video
  const hasImage = !hasVideo && images && images.length > 0
  const hasGallery = (!hasVideo && images && images.length > 1) || (hasVideo && images && images.length > 0)

  return (
    <Layout isSmall colorBlock={LAYOUT_COLOR_BLOCK.WHITE}>
      <Layout.Column>
        <BackLink
          component={Link}
          href={`/${locale}/${COMPANY_BASENAME}/${companySlug}${NEWSFEED_PAGE_ROUTE.path}#${ARTICLE_TITLE_ID}`}
          className={styles.backLink}
        >
          {t("companies_client.newsFeed.articles.details.backLink")}
        </BackLink>
        <div className={styles.tagWrapper}>
          {targeted && (
            <Signature imgSrc={dataCareerCenter?.logo || ""} imgAlt="" className={styles.signature}>
              {t("companies_client.newsFeed.articles.exclusive", { name: dataCareerCenter?.name || "" })}
            </Signature>
          )}
          <Tag size="small">{t(`companies_client.articles_type.${ARTICLE_SUBJECT_TYPES_I18N[subject]}`)}</Tag>
        </div>
        <Heading variant="title2" weight="bold" as="h2">
          {title}
        </Heading>
        <Text variant="body2" as="div" className={styles.articleDate}>
          {getDate({
            date: new Date(createTime),
            dateFormat: DATE_FORMATS.LIGHT_DATE,
            locale,
            timeZone: "Europe/Paris",
          })}
        </Text>
        {hasVideo && (
          <VideoDynamic
            video={video}
            withDescription={false}
            className={styles.articleVideo}
            tracking={{ id: dataCompany?.id, name: dataCompany?.page.name }}
          />
        )}
        {hasImage && <ImageDynamic src={images[0].url} alt={images[0]?.alt || ""} />}
        <RichText tag="div" className={styles.richText} allowTargetAttribute>
          {content}
        </RichText>
        {externUrl && (
          <LinkUiKit href={externUrl} target="_blank" rel="noopener noreferrer" className={styles.link}>
            {externUrl}
          </LinkUiKit>
        )}
        {hasGallery && (
          <ElementGrid className={styles.grid}>
            {images.map(image => {
              return (
                <Image
                  key={image.url}
                  alt={image.alt || ""}
                  src={image.url}
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 250px"
                  width="16"
                  height="9"
                  className={styles.image}
                  role="button"
                  onClick={() => {
                    openModal(image.url)
                  }}
                />
              )
            })}
          </ElementGrid>
        )}
        <Modal onClose={() => closeModal()} isOpen={isModalOpen} wrapperClassName={styles.modalWrapper}>
          <Modal.Header className={styles.modalHeader}>
            <Modal.Title> </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Image src={modalImageSrc} alt="" width="1600" height="900" className={styles.modalImage} />
          </Modal.Body>
        </Modal>
      </Layout.Column>
    </Layout>
  )
}
